import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PHONE_E164 = "447448457089";
const DEFAULT_GREETING = "Hi CV Folks, I'd like to speak to a career consultant.";

/**
 * Large floating "Chat with us" WhatsApp launcher.
 *
 * Replaces the previous Sarah chat launcher globally. The Sarah panel is now
 * only opened when a page explicitly dispatches the `sarah:open` event (e.g.
 * the "Ask Sarah" button on the AI CV Maker page).
 *
 * Phone number and pre-fill greeting come from the admin-editable
 * `/api/settings/public` `escalation` block, with sensible fallbacks.
 */
export default function WhatsAppLauncher() {
  const [phone, setPhone] = useState(DEFAULT_PHONE_E164);
  const [greeting, setGreeting] = useState(DEFAULT_GREETING);
  const [hiddenBySarah, setHiddenBySarah] = useState(false);

  useEffect(() => {
    const handler = (e) => setHiddenBySarah(!!e.detail?.open);
    window.addEventListener("sarah:panel-state", handler);
    return () => window.removeEventListener("sarah:panel-state", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings/public`)
      .then(({ data }) => {
        if (cancelled) return;
        const esc = data?.escalation;
        if (esc?.whatsapp_phone_e164) {
          setPhone(esc.whatsapp_phone_e164.replace(/\D/g, ""));
        }
        if (esc?.whatsapp_greeting) {
          setGreeting(esc.whatsapp_greeting);
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const href = `https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`;

  if (hiddenBySarah) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-launcher"
      aria-label="Chat with us on WhatsApp"
      className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-3 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white pl-4 pr-6 py-3 shadow-2xl ring-2 ring-white transition hover:scale-105 active:scale-100"
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15"
        aria-hidden="true"
      >
        <svg viewBox="0 0 32 32" className="h-6 w-6 fill-current">
          <path d="M19.11 17.35c-.28-.14-1.68-.83-1.94-.92-.26-.09-.45-.14-.63.14-.19.28-.72.92-.88 1.11-.16.19-.32.21-.6.07-.28-.14-1.19-.44-2.27-1.4-.84-.75-1.4-1.68-1.57-1.96-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.87-2.08-.23-.55-.46-.47-.63-.48-.16 0-.35-.02-.53-.02-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.35s1 2.72 1.14 2.91c.14.19 1.97 3.01 4.77 4.22.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.68-.68 1.92-1.34.24-.66.24-1.22.17-1.34-.07-.12-.26-.19-.53-.33zM16.02 5.33c-5.9 0-10.7 4.79-10.7 10.68 0 1.88.5 3.72 1.44 5.34l-1.53 5.6 5.72-1.5c1.56.85 3.32 1.3 5.06 1.3h.01c5.9 0 10.7-4.79 10.7-10.68 0-2.85-1.11-5.53-3.13-7.55-2.02-2.02-4.71-3.14-7.57-3.19z" />
        </svg>
      </span>
      <span className="text-base font-bold leading-tight" data-testid="whatsapp-launcher-label">
        Chat with us
      </span>
    </a>
  );
}
